"use client";

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCurrentUser } from "@/hooks/use-current-user";
import { getCurrentUserCreditBalance } from "@/actions/db/credit-balance";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";
import { NotEnoughCreditsError } from "@/errors/not-enough-credits-error";

type CreditBalanceState = {
  creditBalance: number;
  isFetchingBalance: boolean;
  handleCreditTransaction: (
    transaction: () => Promise<void>,
    price: number,
  ) => Promise<void>;
};

const CreditBalanceContext = createContext<CreditBalanceState | undefined>(
  undefined,
);

export const useCreditBalance = (): CreditBalanceState => {
  if (!CreditBalanceContext) {
    throw new Error("CreditBalanceContext is undefined");
  }

  return useContext(CreditBalanceContext)!;
};

const CreditBalanceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const user = useCurrentUser();
  const logger = useLoggingContext();

  const [creditBalance, setCreditBalance] = useState(0);
  const [isFetchingBalance, setIsFetchingBalance] = useState(false);

  useEffect(() => {
    const handleFetch = async () => {
      try {
        setIsFetchingBalance(true);
        setCreditBalance(await fetchBalance());
      } catch (err) {
        logger.logError(err as Error);
        setCreditBalance(0);
      } finally {
        setIsFetchingBalance(false);
      }
    };

    handleFetch();
  }, [user]);

  const handleCreditTransaction = async (
    transaction: () => Promise<void>,
    price: number,
  ) => {
    const currentBalance = await fetchBalance();
    if (currentBalance < price) {
      throw new NotEnoughCreditsError(currentBalance, price);
    }
    await transaction();
    setCreditBalance(await fetchBalance());
  };

  const fetchBalance = async () => {
    if (!user) {
      return 0;
    }

    return (await getCurrentUserCreditBalance()).balance;
  };

  return (
    <CreditBalanceContext.Provider
      value={{ creditBalance, isFetchingBalance, handleCreditTransaction }}
    >
      {children}
    </CreditBalanceContext.Provider>
  );
};

export default CreditBalanceProvider;
